import { InitMessage, ScenarioDef, SetWedgeMessage, WedgeState } from '../types';
import * as THREE from 'three';

import { Environment } from './Environment';
import { UIState } from '../ui/store';

const BINDING_RESET_DELAY_MS = 200;
const BINDING_UNPINCH_RATIO = 0.5;
const BINDING_UNPINCH_SUSTAINED_MS = 300;
const WEDGE_MIN_UNPINCH_DEPTH = 0.04; // 4cm minimum insertion before wedge relieves saw

export class ScenarioManager {
  private worker: Worker;
  private currentScenario: string | null = null;
  private lastPositions: Float32Array | undefined = undefined;
  private lowPressureStartTime: number | null = null;

  constructor(
    private onSync: (positions: Float32Array, strains?: Float32Array, brokenKerfSprings?: boolean[][], wedges?: WedgeState[]) => void,
    private onTopology: (segments: any[][], kerfs?: any[], springs?: Int32Array, positions?: Float32Array) => void,
    private environment: Environment,
    private onLoadStart?: (scenario: ScenarioDef) => void
  ) {
    // Import worker via Vite syntax
    this.worker = new Worker(new URL('../physics/physics.worker.ts', import.meta.url), { type: 'module' });
    
    this.worker.onmessage = (e: MessageEvent) => {
      const data = e.data;
      if (data.type === 'SYNC') {
        if (data.bindingPressure !== undefined) {
          UIState.updateSimulation('currentPressure', data.bindingPressure);
          const state = UIState.getState();
          const now = performance.now();
          if (data.bindingPressure > state.simulation.bindingThreshold && !state.chainsawStuck) {
            if (now - state.lastResetTime > BINDING_RESET_DELAY_MS) {
              UIState.update({ chainsawStuck: true });
              this.lowPressureStartTime = null;
            }
          } else if (state.chainsawStuck) {
            const hasActiveWedge = data.wedges && data.wedges.some((w: WedgeState) => !w.isFalling && w.insertionDepth > WEDGE_MIN_UNPINCH_DEPTH);
            const unpinchThreshold = state.simulation.bindingThreshold * BINDING_UNPINCH_RATIO;
            if (hasActiveWedge && data.bindingPressure < unpinchThreshold) {
              if (this.lowPressureStartTime === null) {
                this.lowPressureStartTime = now;
              } else if (now - this.lowPressureStartTime > BINDING_UNPINCH_SUSTAINED_MS) {
                UIState.update({ chainsawStuck: false });
                this.lowPressureStartTime = null;
              }
            } else {
              this.lowPressureStartTime = null;
            }
          }
        }
        this.lastPositions = data.positions;
        this.onSync(data.positions, data.strains, data.brokenKerfSprings, data.wedges);
      } else if (data.type === 'TOPOLOGY') {
        this.onTopology(data.segments, data.kerfs, data.springs, this.lastPositions);
      } else if (data.type === 'RESIZE_BUFFERS') {
        const msg = e.data as any;
        console.log(`[ScenarioManager] RESIZE_BUFFERS received: ${msg.nodeCount} nodes.`);
      }
    };
  }

  public async loadScenario(id: string) {
    this.currentScenario = id;
    try {
      const response = await fetch(`${id}?t=${Date.now()}`);
      const scenario: ScenarioDef = await response.json();
      this.loadScenarioObj(scenario);
    } catch (e) {
      console.error('Failed to load scenario:', e);
    }
  }

  public loadScenarioObj(scenario: ScenarioDef) {
    if (!scenario.logs || scenario.logs.length === 0) {
      console.warn('Scenario contains no logs');
      return;
    }
    
    if (this.onLoadStart) {
      this.onLoadStart(scenario);
    }
    
    const terrain = this.environment.generate(scenario);
    
    const initMsg: InitMessage = {
      type: 'INIT',
      logs: scenario.logs,
      terrain: terrain
    };
    
    this.worker.postMessage(initMsg);
    this.setGravity(UIState.getState().simulation.gravity);
    const nameEl = document.getElementById('scenario-name');
    if (nameEl) nameEl.textContent = `Scenario: ${scenario.name || 'Custom'}`;
  }

  public setTelemetry(showPerfWarnings: boolean) {
    this.worker.postMessage({ type: 'SET_TELEMETRY', showPerfWarnings });
  }

  public setGravity(gravity: number) {
    this.worker.postMessage({ type: 'SET_GRAVITY', gravity });
  }

  public setShowTension(showTension: boolean) {
    this.worker.postMessage({ type: 'SET_SHOW_TENSION', showTension });
  }

  public sendCutEvent(point: THREE.Vector3, normal: THREE.Vector3) {
    this.worker.postMessage({
      type: 'INITIATE_CUT',
      planePoint: [point.x, point.y, point.z],
      planeNormal: [normal.x, normal.y, normal.z]
    });
  }

  public setHandForce(active: boolean, point?: THREE.Vector3, direction?: THREE.Vector3, magnitude?: number) {
    this.worker.postMessage({
      type: 'SET_HAND_FORCE',
      active,
      point: point ? [point.x, point.y, point.z] : undefined,
      direction: direction ? [direction.x, direction.y, direction.z] : undefined,
      magnitude
    });
  }

  public sendCutProgress(pos: THREE.Vector3, quat: THREE.Quaternion, scale: THREE.Vector3) {
    this.worker.postMessage({
      type: 'CUT_PROGRESS',
      boxPosition: [pos.x, pos.y, pos.z],
      boxQuaternion: [quat.x, quat.y, quat.z, quat.w],
      boxScale: [scale.x, scale.y, scale.z]
    });
  }

  public sendGuidebarState(active: boolean, pos?: THREE.Vector3, quat?: THREE.Quaternion, scale?: THREE.Vector3) {
    this.worker.postMessage({
      type: 'SET_GUIDEBAR',
      active,
      boxPosition: pos ? [pos.x, pos.y, pos.z] : undefined,
      boxQuaternion: quat ? [quat.x, quat.y, quat.z, quat.w] : undefined,
      boxScale: scale ? [scale.x, scale.y, scale.z] : undefined
    });
  }

  public sendWedgeCommand(msg: SetWedgeMessage) {
    this.worker.postMessage(msg);
  }

  public terminate() {
    this.worker.terminate();
  }
}
